/**
 * User preferences — theme, layout density, and number formatting.
 *
 * Persisted in localStorage (`pp-prefs`) so each browser remembers its
 * own choice. Theme can also follow the OS via `prefers-color-scheme`.
 *
 * Theme is applied to <html data-theme="…"> so CSS rules can target
 * either dark (default) or light.
 *
 * Number format pipes through `useFmt()` which returns a function the
 * same shape as `fmtMoney(n, digits)` so existing call sites barely
 * change ─ when we want a component to honor the user's choice we call
 * `const fmt = useFmt(); fmt(value)` instead of `fmtMoney(value)`.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

const STORAGE_KEY = 'pp-prefs'

// Brand-kit rule: BLUE is the only accent family. The palette below is
// intentionally locked to a single entry so the existing accent-picker UI
// resolves to on-brand colors even if it renders a swatch grid. Every
// downstream --pp-accent / --pp-accent-light consumer therefore stays on
// the canonical Pinnacle primary + primary-light values.
export const ACCENT_PALETTE = {
  blue: { id: 'blue', label: 'Blue', hex: '#41a3dd', light: '#6bc0f0' },
}

export const DEFAULTS = {
  theme: 'dark',          // 'dark' | 'light' | 'system'
  density: 'comfortable', // 'comfortable' | 'compact' | 'spacious'
  numberFormat: 'full',   // 'full' | 'abbreviated'
  fontScale: 'medium',    // 'small' | 'medium' | 'large'
  accentColor: 'blue',    // ACCENT_PALETTE key
  dateFormat: 'us',       // 'us' (Mar 15, 2026) | 'iso' (2026-03-15) | 'short' (03/15)
  reducedMotion: false,   // disable framer-motion / heavy transitions
  ambientBackground: true,// the aurora/grid background gradient
  sidebarDefault: 'expanded', // 'expanded' | 'collapsed'
  defaultRoute: '/dashboard',
}

function readStored() {
  if (typeof window === 'undefined') return DEFAULTS
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULTS
    const parsed = JSON.parse(raw)
    return { ...DEFAULTS, ...parsed }
  } catch {
    return DEFAULTS
  }
}

const PrefsContext = createContext(null)

export function PreferencesProvider({ children }) {
  const [prefs, setPrefs] = useState(DEFAULTS)

  // Hydrate from localStorage on mount (avoid SSR mismatch)
  useEffect(() => { setPrefs(readStored()) }, [])

  // Persist on change + apply visual side-effects to <html>
  useEffect(() => {
    if (typeof window === 'undefined') return
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)) } catch {}
    const root = document.documentElement
    applyTheme(prefs.theme)
    root.setAttribute('data-density', prefs.density)
    root.setAttribute('data-font-scale', prefs.fontScale)
    root.setAttribute('data-accent', prefs.accentColor)
    root.setAttribute('data-reduced-motion', prefs.reducedMotion ? 'true' : 'false')
    root.setAttribute('data-ambient', prefs.ambientBackground ? 'true' : 'false')
    // Brand-locked: --pp-accent is always the Pinnacle blue. Any legacy
    // stored pref (violet/emerald/etc. from earlier releases) is coerced
    // back to blue via the fallback so no off-brand hue leaks in.
    const accent = ACCENT_PALETTE[prefs.accentColor] || ACCENT_PALETTE.blue
    root.style.setProperty('--pp-accent', accent.hex)
    root.style.setProperty('--pp-accent-light', accent.light)
  }, [prefs])

  // Listen for OS theme flips when user picked 'system'
  useEffect(() => {
    if (prefs.theme !== 'system' || typeof window === 'undefined') return
    const mql = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = () => applyTheme('system')
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [prefs.theme])

  const update = useCallback((patch) => setPrefs((p) => ({ ...p, ...patch })), [])
  const reset = useCallback(() => setPrefs(DEFAULTS), [])

  const value = useMemo(() => ({ prefs, update, reset }), [prefs, update, reset])
  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>
}

export function usePrefs() {
  const ctx = useContext(PrefsContext)
  if (!ctx) throw new Error('usePrefs must be used inside <PreferencesProvider>')
  return ctx
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return
  let resolved = theme
  if (theme === 'system') {
    resolved = window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
  }
  document.documentElement.setAttribute('data-theme', resolved)
}

// ── Number formatting helpers ──────────────────────────────────────────

function fmtFull(n, digits = 0) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: digits,
  }).format(n || 0)
}

function fmtAbbreviated(n, digits = 0) {
  const v = Number(n || 0)
  const abs = Math.abs(v)
  const sign = v < 0 ? '-' : ''
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 1 : 2)}M`
  if (abs >= 10_000)    return `${sign}$${(abs / 1_000).toFixed(0)}K`
  if (abs >= 1_000)     return `${sign}$${(abs / 1_000).toFixed(1)}K`
  return fmtFull(v, digits)
}

/**
 * Hook that returns a money formatter following the user's preference.
 * Replaces direct calls to fmtMoney() in components that want to honor
 * the abbreviated/full toggle.
 */
export function useFmt() {
  const { prefs } = usePrefs()
  return useCallback(
    (n, digits = 0) => (prefs.numberFormat === 'abbreviated' ? fmtAbbreviated(n, digits) : fmtFull(n, digits)),
    [prefs.numberFormat]
  )
}

/**
 * Date formatter respecting the user's preference. Accepts a Date or
 * ISO/YYYY-MM-DD string. Falls back to the input string when invalid.
 */
export function useFmtDate() {
  const { prefs } = usePrefs()
  return useCallback((input, opts = {}) => {
    if (!input) return ''
    const d = input instanceof Date ? input : new Date(input)
    if (Number.isNaN(d.getTime())) return String(input)
    if (prefs.dateFormat === 'iso') {
      return d.toISOString().slice(0, 10)
    }
    if (prefs.dateFormat === 'short') {
      return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
    }
    // 'us' default: "Mar 15, 2026"
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: opts.includeYear === false ? undefined : 'numeric',
    })
  }, [prefs.dateFormat])
}
